import React from "react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaBolt,
  FaUsers,
  FaChartLine,
  FaGlobe,
  FaLightbulb,
  FaRocket,
  FaArrowRight,
  FaExchangeAlt,
  FaRobot,
  FaCogs,
  FaLaptopCode,
  FaUserGraduate,
  FaHandsHelping,
  FaCrown,
} from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";

const Careers = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif] overflow-hidden">
      {/* CSS for custom styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&family=Poppins:wght@400;600;700&display=swap');
        
        .soft-shadow {
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
        }
        
        .soft-shadow:hover {
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          transform: translateY(-5px);
          transition: all 0.3s ease;
        }

  .bg-light-gradient {
  background: 
    radial-gradient(circle at 20% 30%, rgba(47, 110, 219, 0.7), transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(47, 110, 219, 0.55), transparent 50%),
    linear-gradient(135deg, #eaf1ff 0%, #dbe7ff 100%);
}
        .divider {
          width: 1px;
          height: 40px;
          background: #e2e8f0;
        }

        .primary-btn {
          background: linear-gradient(135deg, #3B82F6, #2563EB);
          color: white;
        }

        .secondary-btn {
          border: 1px solid #e2e8f0;
          color: #1e293b;
        }

        .secondary-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
      `}</style>

      {/* Hero Section (Light Version) */}
      <section className=" relative pt-32 pb-20 px-6 bg-light-gradient ">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 border border-blue-100">
              <span className="text-blue-600 text-sm font-semibold tracking-wide flex items-center gap-2 uppercase">
                Careers at Xeer Technology
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight text-slate-900">
              Build the Future <br />
              <span className="text-blue-600">with Blockchain & AI</span>
            </h1>

            <p className="text-xl text-slate-600 max-w-xl leading-relaxed">
              Join a team that’s building scalable digital products across AI,
              Blockchain, Web3, and enterprise systems
            </p>
            <p className="text-xl text-slate-600 max-w-xl leading-relaxed">
              We’re not just creating software — we’re shaping the future of
              technology
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <a href="https://wa.me/+917383847632" target="_blank">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href="https://wa.me/+917383847632"
                  target="_blank"
                  className="px-8 py-4 primary-btn rounded-xl font-bold flex items-center gap-3 shadow-lg shadow-blue-500/10"
                >
                  Apply Now <FaArrowRight />
                </motion.button>
              </a>
              <a href="https://t.me/Xeertechnology" target="_blank">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-4 secondary-btn rounded-xl font-bold transition-all"
                >
                  Join Our Team
                </motion.button>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative flex justify-center lg:justify-end"
          >
            <img
              src="/xeer_3d_tech_stack_light.png"
              alt="Xeer Technology Tech Stack"
              className="w-full max-w-[600px] h-auto drop-shadow-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* “Why Choose Us” Section */}
      {/* <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-bold text-slate-900 font-['Poppins']">
              Why Choose Us
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Discover why innovators and builders choose Xeer Technology to
              grow their careers.
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              {
                title: "Innovation Hub",
                icon: <FaLightbulb className="text-blue-500" />,
                points: [
                  "Cutting-edge AI research",
                  "Web3 architecture",
                  "Blockchain protocols",
                  "Rapid prototyping",
                  "Future-proof tech stack",
                ],
                color: "blue",
              },
              {
                title: "Growth Engine",
                icon: <FaChartLine className="text-purple-500" />,
                points: [
                  "Personalized mentorship",
                  "Clear career paths",
                  "Learning stipends",
                  "Leadership workshops",
                  "High-impact projects",
                ],
                color: "purple",
              },
              {
                title: "Global Network",
                icon: <FaGlobe className="text-indigo-500" />,
                points: [
                  "Remote-first culture",
                  "Global client base",
                  "Cross-border teams",
                  "Annual retreats",
                  "Diverse environment",
                ],
                color: "indigo",
              },
              {
                title: "Team Culture",
                icon: <FaUsers className="text-pink-500" />,
                points: [
                  "Radical transparency",
                  "Extreme ownership",
                  "Collaborative spirit",
                  "Flat hierarchy",
                  "Mental wellness support",
                ],
                color: "pink",
              },
            ].map((card, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="bg-white p-8 rounded-3xl soft-shadow border border-slate-100 flex flex-col h-full group"
              >
                <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform shadow-inner">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-6 text-slate-900 font-['Poppins']">
                  {card.title}
                </h3>
                <ul className="space-y-4 flex-grow">
                  {card.points.map((point, pIdx) => (
                    <li
                      key={pIdx}
                      className="flex items-start gap-3 text-slate-500 text-sm leading-relaxed"
                    >
                      <FaCheckCircle className="text-blue-500 shrink-0 mt-1" />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}
      {/*  */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900">
            Why Join Xeer Technology
          </h2>
          <p className="text-slate-500 mt-4">
            At Xeer, you’re not just an employee — you’re part of a team that
            builds, innovates, and grows together.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
          {[
            "Work on cutting-edge AI & Blockchain projects",
            "Collaborative and growth-driven environment",
            "Real-world product development experience",
            "Opportunities to learn, lead, and innovate",
            "Flexible and performance-focused culture",
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 text-slate-600">
              <FaCheckCircle className="text-blue-500 mt-1" />
              <p>{item}</p>
            </div>
          ))}
        </div>
      </section>
      {/*  */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto text-center mb-14">
          <h2 className="text-4xl font-bold text-slate-900">
            What You’ll Work On
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {[
            {
              text: "Crypto exchanges & Web3 platforms",
              icon: <FaExchangeAlt />,
            },
            {
              text: "AI agents & automation systems",
              icon: <FaRobot />,
            },
            {
              text: "ERP / CRM enterprise solutions",
              icon: <FaCogs />,
            },
            {
              text: "SaaS & scalable web applications",
              icon: <FaLaptopCode />,
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center group hover:shadow-xl hover:border-blue-200 transition-all"
            >
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all text-xl">
                {item.icon}
              </div>

              <p className="text-slate-700 font-medium">{item.text}</p>
            </div>
          ))}
        </div>
      </section>
      {/*  */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-slate-50 text-center">
        {/* HEADING */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Our Work Culture
          </h2>
          <p className="text-slate-500">
            Built on strong values that drive innovation, teamwork, and growth.
          </p>
        </div>

        {/* GRID */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          {[
            {
              text: "Innovation over routine",
              icon: <FaLightbulb />,
              color: "from-yellow-400 to-orange-400",
            },
            {
              text: "Collaboration over hierarchy",
              icon: <FaUsers />,
              color: "from-blue-400 to-indigo-500",
            },
            {
              text: "Growth over comfort",
              icon: <FaChartLine />,
              color: "from-green-400 to-emerald-500",
            },
            {
              text: "One team. Shared vision. Real execution.",
              icon: <FaRocket />,
              color: "from-purple-400 to-pink-500",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="group relative p-[1px] rounded-2xl bg-gradient-to-r from-slate-200 to-slate-300 hover:from-indigo-400 hover:to-purple-500 transition-all"
            >
              {/* CARD */}
              <div className="bg-white rounded-2xl p-8 h-full flex items-center gap-4 text-left transition-all group-hover:shadow-xl">
                {/* ICON */}
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-xl text-white bg-gradient-to-r ${item.color} text-xl shrink-0`}
                >
                  {item.icon}
                </div>

                {/* TEXT */}
                <p className="text-slate-700 font-medium leading-relaxed">
                  {item.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/*  */}

      {/*  */}
      <section className="py-24 px-6 bg-gradient-to-b from-slate-50 to-white">
        {/* HEADING */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Growth & Opportunities
          </h2>
          <p className="text-slate-500">
            We invest in your growth — professionally, technically, and
            personally.
          </p>
        </div>

        {/* GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "Skill Development",
              desc: "Continuous learning, mentorship, and upskilling programs",
              icon: <FaUserGraduate />,
              color: "from-blue-500 to-indigo-500",
            },
            {
              title: "Hands-on Experience",
              desc: "Work on real-world, high-impact projects",
              icon: <FaHandsHelping />,
              color: "from-green-500 to-emerald-500",
            },
            {
              title: "Leadership Growth",
              desc: "Opportunities to lead and take ownership",
              icon: <FaCrown />,
              color: "from-yellow-400 to-orange-500",
            },
            {
              title: "Global Exposure",
              desc: "Collaborate with international clients and teams",
              icon: <FaGlobe />,
              color: "from-purple-500 to-pink-500",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="group relative p-[1px] rounded-2xl bg-gradient-to-r from-slate-200 to-slate-300 hover:from-indigo-400 hover:to-purple-500 transition-all"
            >
              {/* CARD */}
              <div className="bg-white rounded-2xl p-8 h-full text-center transition-all group-hover:shadow-xl">
                {/* ICON */}
                <div
                  className={`w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-xl text-white text-xl bg-gradient-to-r ${item.color}`}
                >
                  {item.icon}
                </div>

                {/* TITLE */}
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {item.title}
                </h3>

                {/* DESC */}
                <p className="text-sm text-slate-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="py-24 px-6 bg-slate-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-16 soft-shadow border border-slate-100 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative overflow-hidden">
            <div className="space-y-8 relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                Ready to build the <br />
                <span className="text-blue-600 font-['Poppins']">
                  future with us?
                </span>
              </h2>

              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                We're always looking for talented individuals who are passionate
                about Blockchain, AI, and building scalable digital products.
              </p>

              <div className="flex flex-col gap-4">
                <p className="text-slate-500 flex items-center gap-3">
                  <HiOutlineMail className="text-2xl text-blue-600" />
                  <span className="font-semibold uppercase tracking-wider text-xs text-slate-400">
                    Send your resume to:
                  </span>
                </p>
                <a
                  // href="mailto:sales@xeertechnology.com"
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=sales@xeertechnology.com"
                  target="_blank"
                  className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors"
                >
                  sales@xeertechnology.com
                </a>
              </div>

              {/* <div className="flex flex-wrap gap-4 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="px-8 py-4 primary-btn rounded-xl font-bold shadow-lg shadow-blue-500/10"
                >
                  Apply Now
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="px-8 py-4 secondary-btn rounded-xl font-bold transition-all"
                >
                  Join Our Team
                </motion.button>
              </div> */}
            </div>

            <div className="relative">
              <img
                src="/xeer_team_collaboration_light.png"
                alt="Tech Team Collaborating"
                className="w-full h-auto rounded-2xl drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values / Highlights Row */}
      <section className="py-16 px-6 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-12 lg:gap-6">
          {[
            {
              icon: <FaRocket className="text-blue-500" />,
              title: "Build Real Impact",
              desc: "Solve real-world problems with code",
            },
            {
              icon: <FaBolt className="text-purple-500" />,
              title: "Innovate Together",
              desc: "Collaborate with brilliant global minds",
            },
            {
              icon: <FaChartLine className="text-indigo-500" />,
              title: "Grow Continuously",
              desc: "Upskill and accelerate your career",
            },
            {
              icon: <FaGlobe className="text-pink-500" />,
              title: "Global Exposure",
              desc: "Work with elite clients across borders",
            },
          ].map((item, idx) => (
            <React.Fragment key={idx}>
              <div className="flex items-start gap-4 flex-1 min-w-[250px]">
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-blue-500 shrink-0">
                  {item.icon}
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">
                    {item.title}
                  </h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
              {idx < 3 && <div className="hidden lg:block divider"></div>}
            </React.Fragment>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Careers;
